import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { map } from "rxjs/operators";
import { AutomapperService } from "../../../../../../core/automapper/automapper.service";
import { SessionService } from "../../../../../../core/storage/session.service";
import { FormService } from "../../../../../../dynamic-forms/form.service";
import { Autocomplete } from "../../../../../../dynamic-forms/inputs/autocomplete/autocomplete.model";
import { Dropdown } from "../../../../../../dynamic-forms/inputs/dropdown/dropdown.model";
import { SelectableInput } from "../../../../../../dynamic-forms/inputs/selectable-input.model";
import { TagSearchMultiselect } from "../../../../../../dynamic-forms/inputs/tag-search-multiselect/tag-search-multiselect.model";
import { GridStateService } from "../../../../../../shared/grid/grid-state.service";
import { GridFilter } from "../../../../../../shared/grid/models/grid-filter.model";
import { ArrayHelper } from "../../../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../../../utilities/contracts/number-helper";
import { ObjectHelper } from "../../../../../../utilities/contracts/object-helper";
import { StringHelper } from "../../../../../../utilities/contracts/string-helper";
import { MeasureService } from "../../../../../api/measure/measure.service";
import { NumeratorService } from "../../../../../api/numerator/numerator.service";
import { ENABLE_MA_RADV } from "../../../../member/chase-detail/chase-detail-chart/attributes";
import { ChartService } from "../../../../member/chase-detail/chase-detail-chart/chart.service";
import { NewProjectService } from "../../../../project/new-project/new-project.service";
import { ProjectType } from "../../../../project/project-type.enum";
import { RetrievalPageService } from "../../../../retrieval/retrieval-page/retrieval-page.service";
import { AuditPackageGenerateService } from "../../audit-query-quality/audit-package-generate/audit-package-generate.service";
import { AuditQueryGridService } from "../../audit-query-quality/audit-query-grid/audit-query-grid.service";
import { AuditPackageGenerateRiskService } from "../../audit-query-risk/audit-package-generate-risk/audit-package-generate-risk.service";
import { AuditQueryGridRiskService } from "../../audit-query-risk/audit-query-grid-risk/audit-query-grid-risk.service";
import { AuditQueryGridFilters } from "./audit-query-grid-filter-model";
import { AuditQueryGridFiltersService } from "./audit-query-grid-filters.service";
import { IAuditQueryGridData } from "./member-radv-record.model";
import { IMemberSearchCriteria } from "./member-search-criteria.model";

@Component({
  selector: "app-audit-query-grid-filters",
  templateUrl: "./audit-query-grid-filters.component.html",
  styleUrls: ["./audit-query-grid-filters.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditQueryGridFiltersComponent implements OnInit {
  @Input() stateName = "";
  formGroupQualityFilters: FormGroup;
  formGroupRiskFilters: FormGroup;
  formGroupGeneralFilters: FormGroup;
  projects: Dropdown;
  measures: Dropdown;
  numerators: Dropdown;
  complianceCodes: Dropdown;
  projectTypesInput: Dropdown;
  icdCodeInput: TagSearchMultiselect;
  enrolleeInput: Autocomplete;
  vrcInput: TagSearchMultiselect;
  hccInput: TagSearchMultiselect;
  landingFilters: GridFilter[] = [];
  landingFiltersRisk: AuditQueryGridFilters;
  memberRadvCard: IAuditQueryGridData[] = [];
  private selectedProjectId: number;
  private previousProjectId: number;
  private availableIcdCodes: string[] = [];


  constructor(
    private readonly formService: FormService,
    private readonly retrievalPageService: RetrievalPageService,
    private readonly measureService: MeasureService,
    private readonly numeratorService: NumeratorService,
    private readonly chartService: ChartService,
    private readonly auditQueryGridService: AuditQueryGridService,
    private readonly auditQueryGridRiskService: AuditQueryGridRiskService,
    private readonly auditPackageGenerateService: AuditPackageGenerateService,
    private readonly auditPackageGenerateRiskService: AuditPackageGenerateRiskService,
    private readonly automapper: AutomapperService,
    private readonly gridStateService: GridStateService,
    private newProjectService: NewProjectService,
    private readonly auditQueryGridFiltersService: AuditQueryGridFiltersService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly sessionService: SessionService
  ) { }

  ngOnInit() {
    this.initializeForm();
    this.createForm();
    this.getProjectTypes();
    this.prefillFiltersFromState();
  }

  private initializeForm(): void {
    this.projectTypesInput = new Dropdown({
      key: "ProjectTypesInput",
      label: "Project Type*",
      placeholder: "Select Project Type",
      validators: [Validators.required],
      errorMessages: {
        required: "Project Type is required.",
      },
      appendTo: "body",
    });

    this.projects = new Dropdown({
      key: "ProjectId",
      label: "Project Name*",
      placeholder: "Select a Project...",
      validators: [Validators.required],
      errorMessages: {
        required: "Project is required.",
      },
      appendTo: "body",
    });

    this.measures = new Dropdown({
      key: "MeasureId",
      label: "Measure",
      placeholder: "Select a Measure...",
      appendTo: "body",
    });

    this.numerators = new Dropdown({
      key: "NumeratorId",
      label: "Numerator",
      placeholder: "Select a Numerator...",
      appendTo: "body",
    });

    this.complianceCodes = new Dropdown({
      key: "ComplianceCode",
      label: "Compliance Code",
      placeholder: "Select a Compliance Code...",
      appendTo: "body",
    });

    this.hccInput = new TagSearchMultiselect({
      key: "HccInput",
      label: "HCC",
      placeholder: "Information Not Available",
      maxSelectedLabels: 150,
    });

    this.vrcInput = new TagSearchMultiselect({
      key: "VrcInput",
      label: "VRC Code",
      placeholder: "Information Not Available",
      maxSelectedLabels: 150,
    });

    this.icdCodeInput = new TagSearchMultiselect({
      key: "IcdCodeInput",
      label: "ICD Code",
      placeholder: "Information Not Available",
      maxSelectedLabels: 150,
    });

    this.enrolleeInput = new Autocomplete({
      key: "EnrolleeInput",
      label: "Enrollee*",
      placeholder: "Information Not Available",
      validators: [Validators.required],
      errorMessages: {
        required: "Enrollee is required.",
      },
      searchMinimum: 0,
      showDropdownArrow: false,
      appendTo: "body",
      completeOnFocus: true,
      emptyMessage: "No matches found",
    });

  }

  private isHedisFilterSaved(): boolean {
    return this.prefillFiltersAny && this.projectTypeId === ProjectType.HEDIS && this.landingFilters.some(f => f.key === "ProjectId");
  }

  private isRiskFilterSaved(): boolean {
    return this.prefillFiltersAny && this.projectTypeId === ProjectType.RISK && NumberHelper.isAvailable(this.landingFiltersRisk?.projectId);
  }

  private getProjectList(projectTypeId: number): void {
    const projects$ = projectTypeId === ProjectType.RISK
      ? this.retrievalPageService.getProjectsByAttributes(projectTypeId, ENABLE_MA_RADV.attributeId, "1")
      : this.retrievalPageService
        .getProjectList(undefined, projectTypeId);

    projects$.pipe(map(this.automapper.curryMany("LookupModel", "SelectableInput"))).subscribe(projects => {
      this.projects.options = projects;

      if (this.isHedisFilterSaved() || this.isRiskFilterSaved()) {
        const projectId = this.isHedisFilterSaved()
          ? +this.landingFilters.find(f => f.key === "ProjectId").input.value
          : this.landingFiltersRisk.projectId;
        this.formGroupGeneralFilters.get("ProjectId").setValue(projectId);
        this.projectSelectionChange(projectId);
      }
      this.formService.updateDom.next();
    });
  }

  private getComplianceCodes(): void {
    const isAuditGrid = true;
    this.chartService.getComplianceCodes(isAuditGrid)
      .subscribe(complianceCodes => {
        this.complianceCodes.options = complianceCodes;

        if (this.prefillFiltersAny) {
          const complianceCodeFilter = this.landingFilters.find(f => f.key === "ComplianceCode");
          if (complianceCodeFilter != null) {
            const complianceCode = complianceCodeFilter.input.value;
            this.formGroupQualityFilters.get("ComplianceCode").setValue(complianceCode);
          }

          this.applyFilters();
        }
        this.formService.updateDom.next();
      });
  }

  private getMeasureList(projectId: number): void {
    this.measureService.getMeasuresList(projectId)
      .pipe(map(this.automapper.curryMany("ClinicalMeasureListItem", "SelectableInput")))
      .subscribe(measures => {
        this.measures.options = measures;

        const measureIdFilter = this.landingFilters.find(f => f.key === "MeasureId");
        if (this.prefillFiltersAny && measureIdFilter != null) {
          const measureId = +measureIdFilter.input.value;
          this.formGroupQualityFilters.get("MeasureId").setValue(measureId);

          this.measureSelectionChange(measureId);

        }

        this.formService.updateDom.next();

      });
  }

  private getNumeratorList(measureId: number): void {
    this.numeratorService.getNumeratorList(this.selectedProjectId, measureId)
      .pipe(map(this.automapper.curryMany("NumeratorListItem", "SelectableInput")))
      .subscribe(numerators => {
        this.numerators.options = numerators;

        if (this.prefillFiltersAny) {
          const numeratorIdFilter = this.landingFilters.find(f => f.key === "NumeratorId");
          if (numeratorIdFilter != null) {
            const numeratorId = +numeratorIdFilter.input.value;
            this.formGroupQualityFilters.get("NumeratorId").setValue(numeratorId);
            this.getComplianceCodes();
          } else {
            this.applyFilters();
          }
        }

        this.formService.updateDom.next();
      });
  }

  private getProjectTypes(): void {
    this.newProjectService
      .getProjectTypes()
      .subscribe(options => {
        const filteredOptions = options.filter(option => [ProjectType.HEDIS, ProjectType.RISK].includes(Number(option.value)));
        if (this.projectTypesInput) {
          this.projectTypesInput.options = filteredOptions;
          this.formService.updateDom.next();
          if (this.changeDetector) {
            this.changeDetector.detectChanges();
          }
        }
        if (this.changeDetector) {
          this.changeDetector.markForCheck();
        }
      });
  }

  get prefillFiltersAny(): boolean {
    return this.projectTypeId === ProjectType.HEDIS ? ArrayHelper.isAvailable(this.landingFilters) : !ObjectHelper.isEmpty(this.landingFiltersRisk);
  }

  private prefillFiltersFromState(): void {
    if (StringHelper.isAvailable(this.stateName) && this.gridStateService.hasKey(this.stateName)) {
      const gridState = this.gridStateService.get(this.stateName);
      this.landingFilters = gridState.request.filters.filter(filter => this.getSelectedFilters(filter.input.value));
      if (this.landingFilters.length > 0) {
        if (this.landingFilters.find(f => f.key === "ProjectTypeId") != null) {
          const projectTypeId = +this.landingFilters.find(f => f.key === "ProjectTypeId").input.value;
          this.formGroupGeneralFilters.get("ProjectTypesInput").setValue(projectTypeId);
          this.getProjectList(projectTypeId);
        }
      }
    } else if (this.sessionService.hasSessionKey(`GRID_${this.stateName}_RISK`)) {
      const riskFilteredValues = this.sessionService.get(`GRID_${this.stateName}_RISK`, null);
      this.landingFiltersRisk = new AuditQueryGridFilters({
        projectTypeId: riskFilteredValues.projectTypeId,
        projectId: riskFilteredValues.projectId,
        enrollee: riskFilteredValues.enrollee,
        enrolleeMemberName: riskFilteredValues.enrolleeMemberName,
        enrolleeFirstName: riskFilteredValues.enrolleeFirstName,
        enrolleeLastName: riskFilteredValues.enrolleeLastName,
        enrolleeDateOfBirth: riskFilteredValues.enrolleeDateOfBirth,
        icdCode: riskFilteredValues.icdCode,
        vrcCode: riskFilteredValues.vrcCode,
        hccCode: riskFilteredValues.hccCode,
      });

      this.formGroupGeneralFilters.get("ProjectTypesInput").setValue(this.landingFiltersRisk.projectTypeId);
      this.getProjectList(this.landingFiltersRisk.projectTypeId);

    }
  }

  private getSelectedFilters(filterValue): any {
    if (Array.isArray(filterValue)) {
      return ArrayHelper.isAvailable(filterValue);
    }
    return filterValue;
  }

  projectSelectionChange($event: SelectableInput | number): void {
    const newProjectId = typeof ($event) === "number" ? $event : Number($event.value);
    const projectChanged = this.previousProjectId !== undefined && this.previousProjectId !== null && this.previousProjectId !== newProjectId;

    this.selectedProjectId = newProjectId;

    if (projectChanged && this.projectType === ProjectType.RISK) {
      if (this.formGroupRiskFilters) {
        this.formGroupRiskFilters.get("EnrolleeInput").setValue(null);
      }
      const clearedFilter = new AuditQueryGridFilters({
        projectTypeId: this.projectType,
        projectId: this.selectedProjectId,
      });
      this.auditQueryGridRiskService.resetGridFilter(clearedFilter);
      this.auditQueryGridFiltersService.resetGridFilter(clearedFilter);
      this.auditQueryGridRiskService.sendMemberRadvCardData([]);
    }

    if (!this.prefillFiltersAny) {
      this.clearFilters(false, this.projectType);
    }

    this.previousProjectId = newProjectId;

    if (this.projectType === ProjectType.RISK) {
      if (this.selectedProjectId) {
        const shouldRestoreEnrollee = this.prefillFiltersAny &&
          this.landingFiltersRisk?.enrollee &&
          this.landingFiltersRisk?.projectId === this.selectedProjectId &&
          !projectChanged;
        if (shouldRestoreEnrollee) {
          const enrolleeDisplayText = this.landingFiltersRisk.enrolleeMemberName || `${this.landingFiltersRisk.enrolleeFirstName} ${this.landingFiltersRisk.enrolleeLastName}`;
          const enrolleeValue = {
            text: enrolleeDisplayText,
            value: this.landingFiltersRisk.enrollee,
          };
          this.formGroupRiskFilters.get("EnrolleeInput").setValue(enrolleeValue);
          this.enrolleeInput.placeholder = "Select Enrollee...";
          this.enrolleeSelectionChange(this.landingFiltersRisk.enrollee);
        } else {
          this.enrolleeInput.placeholder = "Select Enrollee...";
        }

        const searchCriteria: IMemberSearchCriteria = {
            sortField: "memberName",
            sortDirection: "DESC",
            projectId: this.selectedProjectId,
        };
        this.auditQueryGridFiltersService.getMembersList(searchCriteria)
            .subscribe(measures => {
                this.enrolleeInput.options = measures
                .sort((a, b) => {
                    const nameA = a.memberName || `${a.memberFirstName} ${a.memberLastName}`;
                    const nameB = b.memberName || `${b.memberFirstName} ${b.memberLastName}`;
                    return nameA.localeCompare(nameB);
                })
                .map(member => ({
                    text: member.memberName || `${member.memberFirstName} ${member.memberLastName}`,
                    value: member.memberId, // Use memberId as the value
                    label: member.memberName || `${member.memberFirstName} ${member.memberLastName}`,
                    disabled: false,
                    extra: null,
                    memberName: member.memberName,
                    memberFirstName: member.memberFirstName,
                    memberLastName: member.memberLastName,
                    memberDateOfBirth: member.memberDateOfBirth,
                }));

                this.formService.updateDom.next();
            });
      } else {
        this.enrolleeInput.placeholder = "Information Not Available";
        this.enrolleeInput.serverFilter = (query, setOptionsFn) => {
          setOptionsFn([]);
        };
      }
    } else {
      this.getMeasureList(this.selectedProjectId);
    }

  }

  measureSelectionChange($event: SelectableInput | number): void {
    const measureId = typeof ($event) === "number" ? $event : Number($event.value);
    this.getNumeratorList(measureId);
  }

  enrolleeSelectionChange($event: SelectableInput | number): void {
    const memberId = typeof ($event) === "number" ? $event : Number($event.value);

    if (NumberHelper.isAvailable(memberId)) {
      this.auditQueryGridFiltersService.getMemberRadvCard(memberId)
        .subscribe({
          next: radvCardData => {
            this.auditQueryGridRiskService.sendMemberRadvCardData(radvCardData);
            const uniqueIcdCodes = this.extractUniqueIcdCodes(radvCardData);
            const uniqueHccCodes = this.extractUniqueHccCodes(radvCardData);
            const uniqueVrcCodes = this.extractUniqueVrcCodes(radvCardData);

            this.populateIcdDropdown(uniqueIcdCodes);
            this.populateHccDropdown(uniqueHccCodes);
            this.populateVrcDropdown(uniqueVrcCodes);

            if (this.prefillFiltersAny) {
              this.applyFilters();
            }
          },
        });

    }
  }

  projectTypeSelectionChange($event: SelectableInput | number): void {
    const projectTypeId = typeof ($event) === "number" ? $event : Number($event.value);
    this.clearFilters(false, projectTypeId);
    this.resetProjects();
    this.getProjectList(projectTypeId);

    // Set the project type in the filters immediately
    const filter = new AuditQueryGridFilters({
      projectTypeId,
    });
    this.auditQueryGridFiltersService.resetGridFilter(filter);

    if (projectTypeId === ProjectType.HEDIS) {
      this.getComplianceCodes();
    }
  }

  get isValid(): boolean {
    if (this.formGroupQualityFilters == null) {
      return false;
    }

    return this.formGroupQualityFilters.valid;
  }

  get isformGroupRiskFiltersValid(): boolean {
    if (this.formGroupRiskFilters == null) {
      return false;
    }

    return this.formGroupRiskFilters.valid;
  }

  get isformGroupGeneralFiltersValid(): boolean {
    if (this.formGroupGeneralFilters == null) {
      return false;
    }

    return this.formGroupGeneralFilters.valid;
  }

  get hasQualityForm(): boolean {
    return this.formGroupQualityFilters != null;
  }

  get hasGeneralForm(): boolean {
    return this.formGroupGeneralFilters != null;
  }

  get hasRiskForm(): boolean {
    return this.formGroupRiskFilters != null;
  }

  get isApplyButtonDisabled(): boolean {
    if (this.isRiskProjectType) {
      return this.isformGroupGeneralFiltersValid && NumberHelper.isAvailable(this.enrollee);
    } else {
      return this.isformGroupGeneralFiltersValid;
    }
  }

  private get projectTypeId(): number {
    if (!this.hasGeneralForm) {
      return null;
    }

    return +this.formGroupGeneralFilters.get("ProjectTypesInput").value;
  }

  private get projectId(): number {
    if (!this.hasGeneralForm) {
      return null;
    }

    return +this.formGroupGeneralFilters.get("ProjectId").value;
  }

  private get measureId(): number | null {
    if (!this.hasQualityForm) {
      return null;
    }

    return this.formGroupQualityFilters.get("MeasureId").value;
  }

  private get numeratorId(): number | null {
    if (!this.hasQualityForm) {
      return null;
    }

    return this.formGroupQualityFilters.get("NumeratorId").value;
  }

  private get complianceCode(): string | null {
    if (!this.hasQualityForm) {
      return null;
    }

    return this.formGroupQualityFilters.get("ComplianceCode").value;
  }

  private get enrollee(): number | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("EnrolleeInput").value;
    if (!value) {
      return null;
    }

    if (typeof value === "object" && value.value !== undefined) {
      return typeof value.value === "string" ? Number(value.value) : value.value;
    }

    return typeof value === "string" ? Number(value) : value;
  }

  private get enrolleeMemberName(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }
    const value = this.formGroupRiskFilters.get("EnrolleeInput").value;
    if (!value || typeof value !== "object") {
      return null;
    }
    return value.memberName || null;
  }

  private get enrolleeFirstName(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("EnrolleeInput").value;
    if (!value || typeof value !== "object") {
      return null;
    }

    return value.memberFirstName || null;
  }

  private get enrolleeLastName(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("EnrolleeInput").value;
    if (!value || typeof value !== "object") {
      return null;
    }

    return value.memberLastName || null;
  }

  private get enrolleeDateOfBirth(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("EnrolleeInput").value;
    if (!value || typeof value !== "object") {
      return null;
    }

    return value.memberDateOfBirth || null;
  }

  private get icdCode(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("IcdCodeInput").value;
    if (!value) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map(item => typeof item === "object" ? item.value : item).join(",");
    }

    if (typeof value === "object" && value.value !== undefined) {
      return value.value;
    }

    return value;
  }

  private get vrcCode(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("VrcInput").value;
    if (!value) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map(item => typeof item === "object" ? item.value : item).join(", ");
    }

    if (typeof value === "object" && value.value !== undefined) {
      return value.value;
    }

    return value;
  }

  private get hcc(): string | null {
    if (!this.hasRiskForm) {
      return null;
    }

    const value = this.formGroupRiskFilters.get("HccInput").value;
    if (!value) {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map(item => typeof item === "object" ? item.value : item).join(",");
    }

    if (typeof value === "object" && value.value !== undefined) {
      return value.value;
    }

    return value;
  }

  get projectType(): number {
    if (!this.hasGeneralForm) {
      return null;
    }

    return this.formGroupGeneralFilters?.get("ProjectTypesInput")?.value || null;
  }

  get isHedisProjectType(): boolean {
    return this.projectType === ProjectType.HEDIS;
  }

  get isRiskProjectType(): boolean {
    return this.projectType === ProjectType.RISK;
  }

  get showEnrolleeInput(): boolean {
    return this.selectedProjectId !== null && this.selectedProjectId !== undefined;
  }

  get showProjectList(): boolean {
    return NumberHelper.isGreaterThan(this.projectTypeId, 0);
  }

  applyFilters(): void {
    let filter: AuditQueryGridFilters;
    if (this.isHedisProjectType) {
      filter = new AuditQueryGridFilters({
        projectTypeId: this.projectTypeId,
        projectId: this.projectId,
        measureId: this.measureId,
        numeratorId: this.numeratorId,
        complianceCode: this.complianceCode,
      });

      this.auditQueryGridService.resetGridFilter(filter);
      this.auditPackageGenerateService.refreshAuditPackageGrid(this.projectId);

    } else {
      filter = new AuditQueryGridFilters({
        projectTypeId: this.projectTypeId,
        projectId: this.projectId,
        enrollee: this.enrollee,
        enrolleeMemberName: this.enrolleeMemberName || this.landingFiltersRisk?.enrolleeMemberName,
        enrolleeFirstName: this.enrolleeFirstName || this.landingFiltersRisk?.enrolleeFirstName,
        enrolleeLastName: this.enrolleeLastName || this.landingFiltersRisk?.enrolleeLastName,
        enrolleeDateOfBirth: this.enrolleeDateOfBirth || this.landingFiltersRisk?.enrolleeDateOfBirth,
        icdCode: this.icdCode,
        vrcCode: this.vrcCode,
        hccCode: this.hcc,
      });

      this.sessionService.put(`GRID_${ this.stateName }_RISK`, filter);
      this.auditQueryGridRiskService.resetGridFilter(filter);
      this.auditPackageGenerateRiskService.refreshAuditPackageGrid(this.projectId);

    }

    this.auditQueryGridFiltersService.resetGridFilter(filter);
  }

  clearFilters(resetProjectSelection: boolean = true, projectType: ProjectType): void {
    this.gridStateService.delete(this.stateName);
    this.sessionService.delete(`GRID_${ this.stateName }_RISK`);
    this.landingFilters = [];
    this.landingFiltersRisk = null;

    if (projectType === ProjectType.RISK) {
      if (resetProjectSelection) {
        this.formGroupGeneralFilters.reset();
      }
      this.formGroupRiskFilters.reset();

      if (this.vrcInput) {
        this.vrcInput.options = [];
        this.vrcInput.placeholder = "Information Not Available";
      }

      if (this.hccInput) {
        this.hccInput.options = [];
        this.hccInput.placeholder = "Information Not Available";
      }

      if (this.icdCodeInput) {
        this.icdCodeInput.options = [];
        this.icdCodeInput.placeholder = "Information Not Available";
      }

      this.auditQueryGridRiskService.resetGridFilter(new AuditQueryGridFilters());
      this.auditPackageGenerateRiskService.refreshAuditPackageGrid(null);
    }

    if (projectType === ProjectType.HEDIS) {
      // Only reset form if we're resetting project selection
      if (resetProjectSelection) {
        this.formGroupGeneralFilters.reset();
        this.projects.options = null;
      }
      this.formGroupQualityFilters.reset();
      this.measures.options = null;
      this.numerators.options = null;

      // Reset VRC and HCC inputs when switching to HEDIS
      if (this.vrcInput) {
        this.vrcInput.options = [];
        this.vrcInput.placeholder = "Information Not Available";
      }
      if (this.hccInput) {
        this.hccInput.options = [];
        this.hccInput.placeholder = "Information Not Available";
      }

      if (this.icdCodeInput) {
        this.icdCodeInput.options = [];
        this.icdCodeInput.placeholder = "Information Not Available";
      }

      this.auditQueryGridService.resetGridFilter(new AuditQueryGridFilters());
      this.auditPackageGenerateService.refreshAuditPackageGrid(null);
    }

    this.auditQueryGridFiltersService.resetGridFilter(new AuditQueryGridFilters());
  }


  private createForm(): void {
    this.formGroupQualityFilters = this.formService.createFormGroup([
      this.measures,
      this.numerators,
      this.complianceCodes,
    ]);
    this.formGroupRiskFilters = this.formService.createFormGroup([
      this.vrcInput,
      this.icdCodeInput,
      this.hccInput,
      this.enrolleeInput,
    ]);
    this.formGroupGeneralFilters = this.formService.createFormGroup([
      this.projects,
      this.projectTypesInput,
    ]);
  }

  private resetProjects(): void {
    if (this.projects) {
      this.projects.options = null;

      if (this.formGroupGeneralFilters) {
        const projectControl = this.formGroupGeneralFilters.get("ProjectId");
        if (projectControl) {
          projectControl.setValue(null);
          projectControl.markAsPristine();
          projectControl.markAsUntouched();
          projectControl.setErrors(null);
        }
      }

      this.selectedProjectId = null;
      this.previousProjectId = null;
    }
  }

  private extractUniqueIcdCodes(radvCardData: IAuditQueryGridData[]): string[] {
    const uniqueIcdCodes = new Set<string>();

    for (const record of radvCardData) {
      if (record.icdCode) {
        uniqueIcdCodes.add(record.icdCode);
      }
    }

    return Array.from(uniqueIcdCodes);
  }

  private extractUniqueHccCodes(radvCardData: IAuditQueryGridData[]): string[] {
    const uniqueHccCodes = new Set<string>();
    radvCardData.forEach(record => {
      if (record.hccCode) {
        record.hccCode.split(",").map(code => code.trim()).forEach(code => {
          if (code) {
            uniqueHccCodes.add(code);
          }
        });
      }
    });
    return Array.from(uniqueHccCodes);
  }

  private extractUniqueVrcCodes(radvCardData: IAuditQueryGridData[]): string[] {
    const uniqueVrcCodes = new Set<string>();
    radvCardData.forEach(record => {
      if (record.validationResultCode) {
        record.validationResultCode.split(",").map(code => code.trim()).forEach(code => {
          if (code) {
            uniqueVrcCodes.add(code);
          }
        });
      }
    });
    return Array.from(uniqueVrcCodes);
  }

  private populateIcdDropdown(uniqueIcdCodes: string[]): void {
    if (uniqueIcdCodes.length > 0) {
      const sortedIcdCodes = [...uniqueIcdCodes].sort((a, b) => a.localeCompare(b));
      this.availableIcdCodes = sortedIcdCodes;

      this.icdCodeInput.options = sortedIcdCodes.map(code => ({
        text: code,
        value: code,
        label: code,
        disabled: false,
        extra: null,
      }));

      if (this.prefillFiltersAny && this.landingFiltersRisk?.icdCode) {
        const selectedValues = this.landingFiltersRisk.icdCode.split(",").map(code => code.trim());
        const selectedOptions = this.icdCodeInput.options.filter(option => selectedValues.includes(String(option.value)));
        this.formGroupRiskFilters.get("IcdCodeInput").setValue(selectedOptions);
        this.icdCodeInput.placeholder = selectedValues.join(", ");
      } else {
        this.icdCodeInput.placeholder = "Select ICD Code...";
      }
    } else {
      this.icdCodeInput.options = [];
      this.icdCodeInput.placeholder = "Information Not Available";
    }

    this.formService.updateDom.next();
    this.changeDetector.detectChanges();

  }

  private populateHccDropdown(uniqueHccCodes: string[]): void {
    if (uniqueHccCodes.length > 0) {
      const sortedHccCodes = [...uniqueHccCodes].sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
      const hccOptions = sortedHccCodes.map(code => ({
        text: code,
        value: code,
        label: code,
        disabled: false,
        extra: null,
      }));

      this.hccInput.options = hccOptions;

      if (this.prefillFiltersAny && this.landingFiltersRisk?.hccCode) {
        const selectedValues = this.landingFiltersRisk.hccCode.split(",").map(code => code.trim());
        const selectedOptions = hccOptions.filter(option => selectedValues.includes(option.value));
        this.formGroupRiskFilters.get("HccInput").setValue(selectedOptions);
        this.hccInput.placeholder = selectedValues.join(", ");
      } else {
        this.hccInput.placeholder = "Select HCC...";
      }
    } else {
      this.hccInput.options = [];
      this.hccInput.placeholder = "Information Not Available";
    }

    this.formService.updateDom.next();
    if (this.changeDetector) {
      this.changeDetector.detectChanges();
    }
  }

  private populateVrcDropdown(uniqueVrcCodes: string[]): void {
    if (uniqueVrcCodes.length > 0) {
      const sortedVrcCodes = [...uniqueVrcCodes].sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });

      const vrcOptions = sortedVrcCodes.map(code => ({
        text: code,
        value: code,
        label: code,
        disabled: false,
        extra: null,
      }));

      this.vrcInput.options = vrcOptions;

      if (this.prefillFiltersAny && this.landingFiltersRisk?.vrcCode) {
        const selectedValues = this.landingFiltersRisk.vrcCode.split(",").map(code => code.trim());
        const selectedOptions = vrcOptions.filter(option => selectedValues.includes(option.value));
        this.formGroupRiskFilters.get("VrcInput").setValue(selectedOptions);
        this.vrcInput.placeholder = selectedValues.join(", ");
      } else {
        this.vrcInput.placeholder = "Select VRC...";
      }
    } else {
      this.vrcInput.options = [];
      this.vrcInput.placeholder = "Information Not Available";
    }

    this.formService.updateDom.next();
    if (this.changeDetector) {
      this.changeDetector.detectChanges();
    }
  }
}
